export type Kind =
  | "group"
  | "array"
  | "jump"
  | "recall"
  | "application"
  | "missing"
  | "count"
  | "addition"
  | "subtraction"
  | "place"
  | "share"
  | "choice"
  | "interactive";
export type Scene = "breakfast" | "garden" | "harbor";
export type Cardinal = "north" | "east" | "south" | "west";
export type EvidenceRole = "recognition" | "construction" | "recall" | "transfer";
export type InteractionSpec =
  | {
      type: "sort";
      criteria: { id: string; label: string; buckets: string[] }[];
      items: { id: string; label: string; icon: string; values: Record<string, string> }[];
      tally: boolean;
      askMore?: boolean;
      parentLabel?: string;
    }
  | { type: "partial-array"; rows: number; each: number; adjustment: number; direction: "more" | "less" }
  | { type: "group-count"; total: number; each: number }
  | { type: "multi-step"; subtotal: number; amount: number; operation: "add" | "subtract" }
  | { type: "compass"; initialFacing: Cardinal; targetFacing: Cardinal; answer: "fixed" | "rotates" | "right" | "left" | "behind" }
  | { type: "route"; width: number; height: number; start: [number, number]; directions: Cardinal[] }
  | { type: "estimate"; actualCm: number; toleranceCm: number; unit: "厘米" | "米"; tool: "直尺" | "卷尺" }
  | { type: "ruler"; start: number; end: number; max: number }
  | { type: "segment"; length: number; max: number }
  | { type: "fact-family"; a: number; b: number }
  | { type: "body-measure"; object: string };
export type InteractionResponse =
  | { type: "sort"; criterion: string; assignments: number[]; counts: number[]; answer?: number | "same" }
  | { type: "group-count"; groups: number }
  | { type: "multi-step"; subtotal: number; result: number; operation: "add" | "subtract" }
  | { type: "partial-array"; direction: "more" | "less"; adjustment: number }
  | { type: "compass"; facing: Cardinal; answer: string }
  | { type: "route"; directions: Cardinal[] }
  | { type: "estimate"; estimateCm: number; unit: string; tool: string }
  | { type: "ruler"; start: number; end: number }
  | { type: "segment"; start: number; end: number }
  | { type: "fact-family"; selected: string[] }
  | { type: "body-measure"; childSpans: number; otherSpans: number; sameObject: boolean; reason: string };
export type AnswerEvidence = {
  version: 3;
  responseMode: "selection" | "manipulation" | "numeric";
  helpLevel: 0 | 1 | 2;
  response?: InteractionResponse | {
    type: "groups" | "array" | "share";
    counts: number[];
    orientation?: "rows" | "columns";
  } | { type: "jump"; hops: number } | { type: "numeric"; value: number; relation?: string };
  explanation: "none" | "sentence-frame";
};
export type Task = {
  id: string;
  kind: Kind;
  a: number;
  b: number;
  scene: Scene;
  fact: string;
  question: string;
  answer: number;
  audio: string;
  foundation?: boolean;
  practiceOnly?: boolean;
  conceptId?: string;
  templateId?: string;
  options?: string[];
  choiceAnswer?: number;
  explanation?: string;
  hintText?: string;
  workedText?: string;
  shareMode?: "each-box" | "group-count";
  evidenceMode?: "understanding" | "calculation" | "application" | "expression";
  evidenceRole?: EvidenceRole;
  contextId?: string;
  interaction?: InteractionSpec;
};
export type Answer = {
  task: Task;
  value: number;
  firstAnswer: number;
  correct: boolean;
  hinted: boolean;
  attempt: number;
  duration: number;
  session: string;
  enteredAnswer?: number;
  relation?: string;
  evidence?: AnswerEvidence;
};
export type Event = {
  id: string;
  at: string;
  type:
    | "answer"
    | "complete"
    | "baseline"
    | "decorate"
    | "profile"
    | "shell"
    | "assessment"
    | "guardian-observation"
    | "checkpoint";
  data: Record<string, unknown>;
};
export type Session = {
  id: string;
  quest: string;
  scene: Scene;
  tasks: Task[];
  index: number;
  started: number;
  mode: "story" | "review" | "baseline" | "assessment" | "challenge" | "course";
  checkpoint?: number;
};
export type FactProgress = {
  key: string;
  seen: boolean;
  understanding: boolean;
  application: boolean;
  days: string[];
  correct: number;
  wrong: number;
  helped: number;
  due: number;
  stage: number;
  status: "new" | "learning" | "initial" | "steady";
  last: number;
  lastRecall: number;
  directions: string[];
};
const CN = ["零", "一", "二", "三", "四", "五", "六", "七", "八", "九"];
export function uuid() {
  if (typeof globalThis.crypto.randomUUID === "function")
    return globalThis.crypto.randomUUID();
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const s = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
}
function hashSeed(seed: string) {
  let hash = 2166136261;
  for (const char of seed) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
export function contentAudioKey(scope: string, text: string) {
  return `${scope}-${hashSeed(`${scope}:${text}`).toString(16).padStart(8, "0")}`;
}
function shuffled<T>(items: T[], seed: string) {
  const result = [...items];
  let state = hashSeed(seed) || 1;
  for (let i = result.length - 1; i > 0; i--) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    const j = state % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
const SCENE_ORDER: Scene[] = ["breakfast", "garden", "harbor"];
const VISUAL_KINDS: Kind[] = ["group", "array", "jump"];
const FOUNDATION_KINDS: Kind[] = ["count", "addition", "subtraction", "place"];
export function chinese(n: number): string {
  if (n < 10) return CN[n];
  if (n === 10) return "十";
  return (
    (n < 20 ? "" : CN[Math.floor(n / 10)]) + "十" + (n % 10 ? CN[n % 10] : "")
  );
}
export const factKey = (a: number, b: number) =>
  `${Math.min(a, b)}x${Math.max(a, b)}`;
export function rhyme(a: number, b: number) {
  const [x, y] = [Math.min(a, b), Math.max(a, b)];
  return (
    CN[x] +
    CN[y] +
    (x * y < 10 ? "得" : "") +
    (x * y === 10 ? "一十" : chinese(x * y))
  );
}
export const FACTS = Array.from({ length: 9 }, (_, a) =>
  Array.from({ length: 9 - a }, (_, b) => ({
    a: a + 1,
    b: a + b + 1,
    key: factKey(a + 1, a + b + 1),
  })),
).flat();
export const ORDER = [2, 5, 3, 4, 6, 7, 8, 9, 1];
// A chapter-aware curriculum order guarantees that every canonical fact is
// encountered, while each chapter starts with a less obvious product instead
// of 1×1. The task kind, scene and review direction remain randomized.
const CURRICULUM_FACTS = [...new Map(
  ORDER.flatMap((factor) =>
    FACTS.filter((f) => f.a === factor || f.b === factor).sort(
      (x, y) =>
        Number(x.a === 1 || x.b === 1) - Number(y.a === 1 || y.b === 1) ||
        y.a * y.b - x.a * x.b,
    ),
  ).map((f) => [f.key, f]),
).values()];
export const CHAPTERS = [
  ["双双好朋友", "两份一样多"],
  ["花园里的小惊喜", "五个五个数"],
  ["早餐店开张啦", "三个一组"],
  ["海边野餐日", "四份小幸福"],
  ["忙碌的小码头", "六个装一箱"],
  ["给朋友的礼物", "七份心意"],
  ["星光音乐会", "八个小音符"],
  ["灯塔亮起来", "九个一排"],
  ["小岛丰收节", "把本领连起来"],
];
export const FRIENDS = [
  { name: "米米", animal: "rabbit", role: "早餐店店长" },
  { name: "团团", animal: "panda", role: "花园守护者" },
  { name: "阿布", animal: "bear", role: "码头搬运员" },
  { name: "啾啾", animal: "bird", role: "小岛邮递员" },
  { name: "小栗", animal: "fox", role: "建造设计师" },
  { name: "慢慢", animal: "turtle", role: "灯塔看守员" },
];
export const SCENES: Record<
  Scene,
  {
    name: string;
    friend: string;
    unit: string;
    item: string;
    classifier: string;
    emoji: string;
    verb: string;
  }
> = {
  breakfast: {
    name: "早餐店",
    friend: "米米",
    unit: "张桌子",
    item: "包子",
    classifier: "个",
    emoji: "🥟",
    verb: "准备早餐",
  },
  garden: {
    name: "小花园",
    friend: "团团",
    unit: "块花田",
    item: "花",
    classifier: "朵",
    emoji: "🌷",
    verb: "种小花",
  },
  harbor: {
    name: "小码头",
    friend: "阿布",
    unit: "艘船",
    item: "货物",
    classifier: "箱",
    emoji: "📦",
    verb: "装货物",
  },
};
export const QUESTS = CHAPTERS.flatMap((c, chapter) =>
  [0, 1, 2].map((part) => ({
    id: `c${chapter + 1}-${part + 1}`,
    chapter,
    part,
    title: [
      `一起${["种下第一排", "装满小花篮", "摆好早餐桌", "布置野餐垫", "整理船舱", "包好礼物", "搭起舞台", "修好灯塔", "准备庆典"][chapter]}`,
      `朋友的新委托`,
      `小岛的新发现`,
    ][part],
    subtitle: c[0],
    factor: ORDER[chapter],
  })),
);
export const BUILDINGS = [
  "早餐店",
  "花园",
  "码头",
  "邮局",
  "树屋",
  "灯塔",
  "长椅",
  "花车",
  "风车",
];
export const day = (time: number | string) =>
  new Date(time).toLocaleDateString("en-CA", { timeZone: "Asia/Shanghai" });
export const answerOf = (t: Task) =>
  t.kind === "interactive"
    ? 1
    : t.kind === "count"
    ? t.a
    : t.kind === "addition"
      ? t.a + t.b
      : t.kind === "subtraction"
        ? t.a
      : t.kind === "place"
          ? t.a * 10 + t.b
          : t.kind === "share"
            ? t.b
            : t.kind === "choice"
              ? Number(t.choiceAnswer)
          : t.kind === "missing"
            ? t.b
            : t.kind === "jump"
              ? t.a * t.b
          : t.a * t.b;

export function interactionIsCorrect(
  spec: InteractionSpec,
  response: InteractionResponse | undefined,
): boolean {
  if (!response || response.type !== spec.type) return false;
  switch (spec.type) {
    case "sort": {
      if (response.type !== "sort" || response.assignments.length !== spec.items.length) return false;
      const criterion = spec.criteria.find((item) => item.id === response.criterion);
      if (!criterion || response.assignments.some((value) => !Number.isInteger(value) || value < 0 || value >= criterion.buckets.length)) return false;
      const assigned = spec.items.every((item, index) => criterion.buckets[response.assignments[index]] === item.values[criterion.id]);
      const counts = criterion.buckets.map((_, bucket) => response.assignments.filter((value) => value === bucket).length);
      const tallyCorrect = !spec.tally || (response.counts.length === counts.length && counts.every((count, index) => response.counts[index] === count));
      const expectedMore = counts[0] === counts[1] ? "same" : counts[0] > counts[1] ? 0 : 1;
      return assigned && tallyCorrect && (!spec.askMore || response.answer === expectedMore);
    }
    case "partial-array":
      return response.type === "partial-array" && response.direction === spec.direction && response.adjustment === spec.adjustment;
    case "group-count":
      return response.type === "group-count" && response.groups === spec.total / spec.each;
    case "multi-step":
      return response.type === "multi-step" && response.subtotal === spec.subtotal && response.result === (spec.operation === "add" ? spec.subtotal + spec.amount : spec.subtotal - spec.amount) && response.operation === spec.operation;
    case "compass":
      return response.type === "compass" && response.facing === spec.targetFacing && response.answer === spec.answer;
    case "route":
      return response.type === "route" && response.directions.length === spec.directions.length && response.directions.every((direction, index) => direction === spec.directions[index]);
    case "estimate":
      return response.type === "estimate" && response.unit === spec.unit && response.tool === spec.tool && Math.abs(response.estimateCm - spec.actualCm) <= spec.toleranceCm;
    case "ruler":
      return response.type === "ruler" && response.start === spec.start && response.end === spec.end && response.end - response.start === spec.end - spec.start;
    case "segment":
      return response.type === "segment" && response.start >= 0 && response.end <= spec.max && response.end - response.start === spec.length;
    case "fact-family": {
      if (response.type !== "fact-family") return false;
      const expected = [`mul-${spec.a}-${spec.b}`, `mul-${spec.b}-${spec.a}`, `div-${spec.a * spec.b}-${spec.a}`, `div-${spec.a * spec.b}-${spec.b}`].sort();
      return response.selected.length === expected.length && [...response.selected].sort().every((value, index) => value === expected[index]);
    }
    case "body-measure":
      return response.type === "body-measure" && Number.isInteger(response.childSpans) && response.childSpans > 0 && Number.isInteger(response.otherSpans) && response.otherSpans > 0 && response.sameObject && response.reason === "span-size";
  }
}
const CARDINALS: Cardinal[] = ["north", "east", "south", "west"];
function validInteractionSpec(value: unknown): value is InteractionSpec {
  if (!value || typeof value !== "object") return false;
  const s = value as InteractionSpec;
  switch (s.type) {
    case "sort":
      return Array.isArray(s.criteria) && s.criteria.length > 0 && s.criteria.length <= 4 &&
        s.criteria.every((c) => typeof c.id === "string" && c.id.length <= 24 && Array.isArray(c.buckets) && c.buckets.length >= 2 && c.buckets.length <= 6 && c.buckets.every((b) => typeof b === "string" && b.length <= 24)) &&
        Array.isArray(s.items) && s.items.length > 0 && s.items.length <= 20 && s.items.every((item) => typeof item.id === "string" && typeof item.label === "string" && item.label.length <= 60 && typeof item.icon === "string" && item.icon.length <= 8 && item.values && typeof item.values === "object" && Object.keys(item.values).length <= s.criteria.length && s.criteria.every((c) => c.buckets.includes(item.values[c.id]))) && typeof s.tally === "boolean" && (s.askMore === undefined || typeof s.askMore === "boolean") && (s.parentLabel === undefined || (typeof s.parentLabel === "string" && s.parentLabel.length <= 40));
    case "partial-array":
      return Number.isInteger(s.rows) && s.rows >= 1 && s.rows <= 9 && Number.isInteger(s.each) && s.each >= 1 && s.each <= 9 && Number.isInteger(s.adjustment) && s.adjustment >= 1 && s.adjustment <= 9 && ["more", "less"].includes(s.direction);
    case "group-count":
      return Number.isInteger(s.total) && s.total > 0 && s.total <= 81 && Number.isInteger(s.each) && s.each > 0 && s.each <= 9 && s.total % s.each === 0;
    case "multi-step":
      return Number.isInteger(s.subtotal) && s.subtotal >= 0 && s.subtotal <= 999 && Number.isInteger(s.amount) && s.amount >= 0 && s.amount <= 99 && ["add", "subtract"].includes(s.operation) && (s.operation !== "subtract" || s.subtotal >= s.amount);
    case "compass":
      return CARDINALS.includes(s.initialFacing) && CARDINALS.includes(s.targetFacing) && typeof s.answer === "string" && s.answer.length <= 16;
    case "route": {
      if (!(Number.isInteger(s.width) && s.width >= 2 && s.width <= 8 && Number.isInteger(s.height) && s.height >= 2 && s.height <= 8 && Array.isArray(s.start) && s.start.length === 2 && s.start.every((n) => Number.isInteger(n) && n >= 0 && n < 8) && Array.isArray(s.directions) && s.directions.length > 0 && s.directions.length <= 12 && s.directions.every((d) => CARDINALS.includes(d)))) return false;
      let x = s.start[0];
      let y = s.start[1];
      if (x >= s.width || y >= s.height) return false;
      for (const direction of s.directions) {
        x += direction === "east" ? 1 : direction === "west" ? -1 : 0;
        y += direction === "south" ? 1 : direction === "north" ? -1 : 0;
        if (x < 0 || y < 0 || x >= s.width || y >= s.height) return false;
      }
      return true;
    }
    case "estimate":
      return Number.isFinite(s.actualCm) && s.actualCm > 0 && s.actualCm <= 1000 && Number.isFinite(s.toleranceCm) && s.toleranceCm >= 0 && s.toleranceCm <= 100 && ["厘米", "米"].includes(s.unit) && ["直尺", "卷尺"].includes(s.tool);
    case "ruler":
      return Number.isInteger(s.start) && Number.isInteger(s.end) && Number.isInteger(s.max) && s.start >= 0 && s.end > s.start && s.max <= 100 && s.end <= s.max;
    case "segment":
      return Number.isInteger(s.length) && s.length > 0 && s.length <= 100 && Number.isInteger(s.max) && s.max >= s.length && s.max <= 100;
    case "fact-family":
      return Number.isInteger(s.a) && s.a >= 1 && s.a <= 9 && Number.isInteger(s.b) && s.b >= 1 && s.b <= 9;
    case "body-measure":
      return typeof s.object === "string" && s.object.length > 0 && s.object.length <= 40;
    default:
      return false;
  }
}
function validInteractionResponse(value: unknown, spec: InteractionSpec): value is InteractionResponse {
  if (!value || typeof value !== "object" || (value as { type?: string }).type !== spec.type) return false;
  const r = value as InteractionResponse;
  switch (spec.type) {
    case "sort":
      return r.type === "sort" && typeof r.criterion === "string" && r.criterion.length <= 24 && Array.isArray(r.assignments) && r.assignments.length === spec.items.length && r.assignments.every((n) => Number.isInteger(n) && n >= -1 && n < 6) && Array.isArray(r.counts) && r.counts.length <= 6 && r.counts.every((n) => Number.isInteger(n) && n >= -1 && n <= 20) && (r.answer === undefined || r.answer === "same" || r.answer === 0 || r.answer === 1);
    case "partial-array":
      return r.type === "partial-array" && ["more", "less"].includes(r.direction) && Number.isInteger(r.adjustment) && r.adjustment >= 0 && r.adjustment <= 9;
    case "group-count":
      return r.type === "group-count" && Number.isInteger(r.groups) && r.groups >= 0 && r.groups <= 81;
    case "multi-step":
      return r.type === "multi-step" && Number.isInteger(r.subtotal) && r.subtotal >= 0 && r.subtotal <= 999 && Number.isInteger(r.result) && r.result >= 0 && r.result <= 999 && ["add", "subtract"].includes(r.operation);
    case "compass":
      return r.type === "compass" && CARDINALS.includes(r.facing) && typeof r.answer === "string" && r.answer.length <= 16;
    case "route":
      return r.type === "route" && Array.isArray(r.directions) && r.directions.length <= 12 && r.directions.every((d) => CARDINALS.includes(d));
    case "estimate":
      return r.type === "estimate" && Number.isFinite(r.estimateCm) && r.estimateCm > 0 && r.estimateCm <= 1000 && typeof r.unit === "string" && r.unit.length <= 8 && typeof r.tool === "string" && r.tool.length <= 12;
    case "ruler":
      return r.type === "ruler" && Number.isInteger(r.start) && Number.isInteger(r.end) && r.start >= 0 && r.start <= spec.max && r.end >= 0 && r.end <= spec.max;
    case "segment":
      return r.type === "segment" && Number.isInteger(r.start) && Number.isInteger(r.end) && r.start >= 0 && r.start <= spec.max && r.end >= 0 && r.end <= spec.max;
    case "fact-family":
      return r.type === "fact-family" && Array.isArray(r.selected) && r.selected.length <= 8 && r.selected.every((id) => typeof id === "string" && id.length <= 40);
    case "body-measure":
      return r.type === "body-measure" && Number.isInteger(r.childSpans) && r.childSpans >= 0 && r.childSpans <= 200 && Number.isInteger(r.otherSpans) && r.otherSpans >= 0 && r.otherSpans <= 200 && typeof r.sameObject === "boolean" && typeof r.reason === "string" && r.reason.length <= 32;
  }
}
export function makeTask(
  kind: Kind,
  a: number,
  b: number,
  scene: Scene,
  id: string,
  foundation = false,
): Task {
  const s = SCENES[scene];
  const total = a * b;
  const question =
    kind === "share"
      ? `野餐带来${a * b}块点心，平均放进${a}个盒子。每盒放一样多，怎么分？`
      : kind === "subtraction"
      ? `原来有${a + b}颗贝壳，拿走${b}颗，还剩几颗？`
      : kind === "place"
        ? `${a}捆小棒，每捆10根，另有${b}根。一共有多少根？`
        : kind === "count"
          ? `数一数，一共有几颗贝壳？`
          : kind === "addition"
            ? `原来有${a}颗贝壳，又找到${b}颗，现在有几颗？`
            : kind === "group"
              ? `准备${a}${s.unit}，每${s.unit}放${b}${s.classifier}${s.item}。请你摆一摆。`
              : kind === "array"
                ? `拼出${a}排，每排${b}个的点阵。`
                : kind === "jump"
                  ? `每次跳${b}格，跳${a}次，会到哪里？`
                  : kind === "missing"
                    ? `${a} × □ = ${total}，方框里藏着几？`
                    : kind === "application"
                      ? `${s.friend}要为${a}${s.unit}各准备${b}${s.classifier}${s.item}。一共需要多少${s.classifier}${s.item}？`
                      : `${a} × ${b} = ？`;
  return {
    id,
    kind,
    a,
    b,
    scene,
    fact: factKey(a, b),
    question,
    answer:
      kind === "interactive"
        ? 1
        : kind === "count" || kind === "subtraction"
        ? a
      : kind === "place"
          ? a * 10 + b
          : kind === "addition"
            ? a + b
            : kind === "share"
              ? b
            : kind === "missing"
              ? b
              : total,
    audio: `${kind}-${a}-${b}-${scene}`,
    foundation,
    conceptId:
      kind === "share"
        ? "DIV-01"
        : kind === "group"
          ? "MUL-01"
          : kind === "array"
            ? "MUL-02"
            : kind === "jump" || kind === "recall" || kind === "missing"
              ? a > 6 || b > 6
                ? "MUL-05"
                : "MUL-03"
            : kind === "application"
                ? "APP-01"
                : kind === "choice" || kind === "interactive"
                  ? "APP-01"
                : undefined,
    evidenceMode:
      kind === "share" || kind === "group" || kind === "array" || kind === "jump"
        ? "understanding"
        : kind === "recall" || kind === "missing"
          ? "calculation"
          : kind === "application"
            ? "application"
            : undefined,
    ...(kind === "share" ? { shareMode: "each-box" as const } : {}),
  };
}

export function makePicnicSession(): Session {
  const id = uuid();
  const tasks = prepareTasks([
    makeTask("share", 3, 4, "breakfast", "picnic-share"),
    makeTask("recall", 3, 4, "garden", "picnic-check"),
    makeTask("application", 3, 4, "harbor", "picnic-transfer"),
  ]);
  tasks[0].question = "小兔带来12块点心和3个盒子，帮大家平均分一分吧。";
  tasks[0].audio = contentAudioKey("curriculum", tasks[0].question);
  tasks[0].hintText = "先选一个盒子放一块，平均分时每个盒子最后都要一样多。";
  tasks[1].question = "每盒4块，3个盒子一共多少块？用乘法检查。";
  tasks[1].audio = contentAudioKey("curriculum", tasks[1].question);
  tasks[2].question = "码头也有3艘小船，每艘装4箱，一共要准备多少箱？";
  tasks[2].audio = contentAudioKey("curriculum", tasks[2].question);
  return {
    id,
    quest: "c4-1",
    scene: "breakfast",
    tasks,
    index: 0,
    started: Date.now(),
    mode: "course",
  };
}

export const CURRICULUM_AREAS = [
  { id: "U1-SORT", title: "整理小集市", subtitle: "分类与数据", templates: ["SORT-COUNT", "SORT-REFINE"] },
  { id: "U2-MULT-1-6", title: "乘法花园", subtitle: "理解等组、阵列和口诀", templates: ["GROUPS-TO-MUL", "ARRAY-TWO-VIEWS", "FACT-MIXED", "PARTIAL-ARRAY", "WORDING-DISTINCTION"] },
  { id: "U3-DIV-1-6", title: "分享厨房", subtitle: "平均分的两种问法", templates: ["DIV-SHARE", "DIV-GROUPS", "FACT-FAMILY", "DIV-MODEL-CHOICE"] },
  { id: "U4-DIRECTION", title: "校园小导游", subtitle: "看方向、找地点、走路线", templates: ["MAP-ROTATE", "MAP-ROUTE", "MAP-RELATION"] },
  { id: "U5-LENGTH", title: "测量工坊", subtitle: "身体尺子、厘米米和线段", templates: ["BODY-MEASURE", "UNIT-ESTIMATE", "TOOL-CHOICE", "RULER-START-END", "RULER-READ", "SEGMENT-DRAW"] },
  { id: "U6-MULT-DIV-7-9", title: "乘除码头", subtitle: "推导7～9口诀并灵活求商", templates: ["BUILD-FACT-PATTERN"] },
  { id: "U7-REVIEW", title: "小岛庆典", subtitle: "选择算法、说明并检查", templates: ["TWO-STEP", "ERROR-DETECTIVE", "WORD-PROBLEM-SELECT"] },
] as const;

const CHOICE_CONTENT: Record<string, {
  conceptId: string;
  evidenceMode?: "understanding" | "application";
  question: (a: number, b: number) => string;
  options: (a: number, b: number) => string[];
  answer: (a: number, b: number) => number;
  explain: (a: number, b: number) => string;
}> = {
  "SORT-COUNT": { conceptId: "SORT-02", evidenceMode: "understanding", question: (a,b) => `集市里有${a}个红风车和${b}个蓝风车，哪种更多？`, options: () => ["红风车更多", "蓝风车更多", "两种一样多"], answer: (a,b) => a>b?0:a<b?1:2, explain: (a,b) => a>b?`${a}>${b}，所以红风车更多；数完还要检查总数。`:a<b?`${b}>${a}，所以蓝风车更多；数完还要检查总数。`:`两种都是${a}个，一样多。` },
  "SORT-REFINE": { conceptId: "SORT-01", evidenceMode: "understanding", question: () => "玩具已经按动物种类分好，想继续整理，哪种做法使用了清楚的新标准？", options: () => ["每一类再按颜色分", "这一堆按大小、下一堆按喜欢程度", "把几个玩具随手放在一起"], answer: () => 0, explain: () => "同一层使用一个一致的标准，之后还可以换标准再分。" },
  "PARTIAL-ARRAY": { conceptId: "MUL-04", evidenceMode: "understanding", question: (a,b) => `${a}排点心，每排${b}块，盘外还多出2块。哪个算式表示总数？`, options: (a,b) => [`${a}×${b}+2`, `${a}×${b}-2`, `${a}+${b}+2`], answer: () => 0, explain: (a,b) => `先数完整的${a}组、每组${b}块，再加上多出的2块。` },
  "WORDING-DISTINCTION": { conceptId: "MUL-01", evidenceMode: "understanding", question: (a,b) => `“${a}个${b}相加”可以简写成什么？`, options: (a,b) => [`${a}+${b}`, `${a}×${b}`, `${a}-${b}`], answer: () => 1, explain: (a,b) => `把${b}重复相加${a}次，可以简写为${a}×${b}。` },
 "DIV-MODEL-CHOICE": { conceptId: "DIV-02", question: (a,b) => `有${a*b}块点心，每盒放${a}块，想知道能装几盒，应怎样做？`, options: (a,b) => [`${a*b}平均分成${b}份`, `每盒${a}块，数一数有几盒`, `把${a*b}减去${a}`], answer: () => 1, explain: (a,b) => `每份大小${a}固定，要数有几份；${a*b}÷${a}=${b}盒。` },
  "FACT-FAMILY": { conceptId: "DIV-03", evidenceMode: "understanding", question: (a,b) => `已知${a}×${b}=${a*b}，哪道除法也能用这句口诀来想？`, options: (a,b) => [`${a*b}÷${a}=${b}`, `${a*b}÷${b}=${a}`, "两道都可以"], answer: () => 2, explain: (a,b) => `同一句口诀能帮助求两个商：${a*b}÷${a}=${b}，${a*b}÷${b}=${a}。` },
  "MAP-ROTATE": { conceptId: "DIR-01", evidenceMode: "understanding", question: () => "地图上北方在上面。小兔转身朝东，地图上的北方会跟着转吗？", options: () => ["会，北方变成右边", "不会，地图北方仍在上面", "要看小兔的左手"], answer: () => 1, explain: () => "地图方向固定；人物转身只会改变人物自己的前后左右。" },
  "MAP-ROUTE": { conceptId: "DIR-02", evidenceMode: "understanding", question: () => "从花园向东走到小桥，再向北走到灯塔。走完后，灯塔在小桥的哪个方向？", options: () => ["北方", "南方", "西方"], answer: () => 0, explain: () => "最后一段路线是从小桥向北，所以灯塔在小桥北方。" },
  "UNIT-ESTIMATE": { conceptId: "MEAS-02", evidenceMode: "understanding", question: (a,b) => `小铅笔长约${10+a}，选什么单位更合理？`, options: () => ["厘米", "米", "千克"], answer: () => 0, explain: () => "短小物体通常用厘米测量，先估再量能检查结果是否合理。" },
  "RULER-START-END": { conceptId: "MEAS-03", evidenceMode: "understanding", question: (a,b) => `尺子从${a}厘米刻度量到${a+b}厘米刻度，物体有多长？`, options: (a,b) => [`${a+b}厘米`, `${b}厘米`, `${a}厘米`], answer: () => 1, explain: (a,b) => `非0刻度起量，要用终点${a+b}减起点${a}，得到${b}厘米。` },
  "SEGMENT-DRAW": { conceptId: "MEAS-04", evidenceMode: "understanding", question: (a,b) => `要画一条${a+b}厘米长的线段，哪种图形符合线段的特征？`, options: () => ["直直的，两头各有一个端点", "弯弯的，没有端点", "直直的，只有一个端点"], answer: () => 0, explain: (a,b) => `线段是直的，并且有两个端点；画好后还要用尺核对${a+b}厘米。` },
  "BODY-MEASURE": { conceptId: "MEAS-01", evidenceMode: "understanding", question: (a,b) => `你量桌子用了${a}拃，爸爸量用了${b}拃，数字不同最可能是为什么？`, options: () => ["桌子一会儿变长一会儿变短", "每个人一拃的长度不同", "其中一个人一定量错了"], answer: () => 1, explain: () => "手掌大小不同，一拃长度就不同；需要公平比较时要用统一单位。" },
  "TWO-STEP": { conceptId: "APP-01", evidenceMode: "understanding", question: (a,b) => `${a}个盒子，每盒${b}块点心，再拿来2块。现在一共多少块？`, options: (a,b) => [`${a}×${b}+2`, `${a}+${b}+2`, `${a}×(${b}+2)`], answer: () => 0, explain: (a,b) => `先算${a}盒、每盒${b}块，再把另外2块加上。` },
  "ERROR-DETECTIVE": { conceptId: "MUL-04", question: (a,b) => `${a}排、每排${b}颗，少了1颗。伙伴写成${a}×${b}+1，哪里错了？`, options: () => ["少了1颗应该减1", "排数和每排颗数不能相乘", "没有错"], answer: () => 0, explain: (a,b) => `完整阵列是${a}×${b}，少1颗要减1，不能加1。` },
  "MAP-RELATION": { conceptId: "DIR-01", evidenceMode: "application", question: () => "面对北方时，东方在你的哪一边？", options: () => ["右边", "左边", "身后"], answer: () => 0, explain: () => "先确定观察者朝北，再辨认左右；地图上的东西南北不随人物转动。" },
  "TOOL-CHOICE": { conceptId: "MEAS-02", evidenceMode: "application", question: () => "想量一量操场边一段比较长的距离，什么工具更合适？", options: () => ["卷尺", "短短的橡皮", "一颗纽扣"], answer: () => 0, explain: () => "工具要和物体的长度相匹配；长距离用卷尺更方便。" },
  "RULER-READ": { conceptId: "MEAS-03", evidenceMode: "application", question: (a,b) => `一支笔从尺子的0刻度开始，笔尖对着${a+b}厘米刻度，笔长多少？`, options: (a,b) => [`${a+b}厘米`, `${b}厘米`, `${a}厘米`], answer: (a,b) => 0, explain: (a,b) => `从0开始量，直接读终点${a+b}厘米。` },
  "WORD-PROBLEM-SELECT": { conceptId: "APP-01", question: (a,b) => `每桌摆${b}个杯子，${a}张桌子还要平均分给2个区域。先求什么？`, options: (a,b) => [`先求${a}桌一共多少杯，再分成2份`, `先把${a}和${b}相加`, `直接用${b}减${a}`], answer: () => 0, explain: (a,b) => `先根据相同的每桌数量求总数，再按两个区域平均分。` },
};

export const CHOICE_TEMPLATE_IDS = Object.keys(CHOICE_CONTENT);

const OBJECTS = ["🪁", "🧸", "🚗", "🧩", "🪀", "🎈", "🪁", "🧸", "🚗", "🧩", "🪀", "🎈"];
function templateInteraction(templateId: string, a: number, b: number, variant: string): InteractionSpec | undefined {
  if (templateId === "SORT-COUNT") {
    const items = [
      ...Array.from({ length: a }, (_, i) => ({ id: `red-${i}`, label: "红风车", icon: "🔴", values: { color: "红色" } })),
      ...Array.from({ length: b }, (_, i) => ({ id: `blue-${i}`, label: "蓝风车", icon: "🔵", values: { color: "蓝色" } })),
    ];
    return { type: "sort", criteria: [{ id: "color", label: "颜色", buckets: ["红色", "蓝色"] }], items, tally: true, askMore: true };
  }
  if (templateId === "SORT-REFINE") {
    const count = Math.max(6, 6 + (hashSeed(variant) % 5));
    const criteria = [{ id: "color", label: "颜色", buckets: ["红色", "蓝色"] }, { id: "size", label: "大小", buckets: ["大", "小"] }];
    if (hashSeed(`${variant}:criterion`) % 2) criteria.reverse();
    const items = Array.from({ length: count }, (_, i) => ({
      id: `toy-${i}`,
      label: `第${i + 1}件玩具`,
      icon: ["🐰", "🐼", "🐻", "🐱", "🐶", "🦊"][i % 6],
      values: { color: i % 2 ? "蓝色" : "红色", size: i % 2 ? "小" : "大" },
    }));
    return { type: "sort", criteria, items, tally: false, parentLabel: "动物玩具组" };
  }
  if (templateId === "PARTIAL-ARRAY" || templateId === "ERROR-DETECTIVE")
    return { type: "partial-array", rows: a, each: b, adjustment: templateId === "ERROR-DETECTIVE" ? 1 : 2, direction: templateId === "ERROR-DETECTIVE" ? "less" : "more" };
  if (templateId === "DIV-MODEL-CHOICE") return { type: "group-count", total: a * b, each: a };
  if (templateId === "FACT-FAMILY") return { type: "fact-family", a, b };
  if (templateId === "MAP-ROTATE") return { type: "compass", initialFacing: "north", targetFacing: "east", answer: "fixed" };
  if (templateId === "MAP-RELATION") return { type: "compass", initialFacing: "north", targetFacing: "north", answer: "right" };
  if (templateId === "MAP-ROUTE") return { type: "route", width: 4, height: 4, start: [0, 2], directions: ["east", "north"] };
  if (templateId === "UNIT-ESTIMATE") return { type: "estimate", actualCm: 10 + a, toleranceCm: 2, unit: "厘米", tool: "直尺" };
  if (templateId === "TOOL-CHOICE") return { type: "estimate", actualCm: 500, toleranceCm: 100, unit: "米", tool: "卷尺" };
  if (templateId === "RULER-START-END") return { type: "ruler", start: a, end: a + b, max: a + b };
  if (templateId === "RULER-READ") return { type: "ruler", start: 0, end: a + b, max: a + b };
  if (templateId === "SEGMENT-DRAW") return { type: "segment", length: a + b, max: a + b + 4 };
  if (templateId === "BODY-MEASURE") return { type: "body-measure", object: "桌子的一条边" };
  if (templateId === "TWO-STEP") return { type: "multi-step", subtotal: a * b, amount: 2, operation: "add" };
  return undefined;
}

const CONSTRUCTION_TEMPLATES = new Set([
  "SORT-COUNT", "SORT-REFINE", "GROUPS-TO-MUL", "ARRAY-TWO-VIEWS", "PARTIAL-ARRAY",
  "DIV-SHARE", "DIV-GROUPS", "DIV-MODEL-CHOICE", "FACT-FAMILY", "MAP-ROTATE", "MAP-ROUTE",
  "UNIT-ESTIMATE", "RULER-START-END", "SEGMENT-DRAW", "BODY-MEASURE", "BUILD-FACT-PATTERN",
]);
const TRANSFER_TEMPLATES = new Set([
  "TWO-STEP", "ERROR-DETECTIVE", "MAP-RELATION", "TOOL-CHOICE", "RULER-READ",
]);

export function makeChoiceTemplateTask(
  templateId: string,
  a: number,
  b: number,
  instanceId = `${templateId}-${a}-${b}`,
): Task {
  const content = CHOICE_CONTENT[templateId];
  if (!content) throw new Error(`缺少模板：${templateId}`);
  const options = content.options(a, b);
  const choiceAnswer = content.answer(a, b);
  const question = content.question(a, b);
  const interaction = templateInteraction(templateId, a, b, instanceId);
  const task = makeTask(interaction ? "interactive" : "choice", a, b, "garden", instanceId);
  const evidenceRole = TRANSFER_TEMPLATES.has(templateId)
    ? "transfer"
    : CONSTRUCTION_TEMPLATES.has(templateId)
      ? "construction"
        : "recognition";
  return {
    ...task,
    id: instanceId,
    templateId,
    conceptId: content.conceptId,
    question,
    ...(interaction ? {} : { options, choiceAnswer }),
    answer: interaction ? 1 : choiceAnswer,
    explanation: content.explain(a, b),
    workedText: content.explain(a, b),
    evidenceMode: content.evidenceMode || (templateId === "TWO-STEP" ? "understanding" : "application"),
    hintText: ({
      "SORT-COUNT": "把同一种颜色的风车放在一起，再一组一组数。",
      "SORT-REFINE": "先说清楚第二次要用同一个什么标准来分。",
      "PARTIAL-ARRAY": "先数完整的几排，再把多出或少掉的部分加上或减掉。",
      "WORDING-DISTINCTION": "想一想“相乘”和“相加”表示的数量关系一样吗？",
      "DIV-MODEL-CHOICE": "题目已知每盒有几块，看看要找的是每块数量还是盒子份数。",
      "FACT-FAMILY": "乘法两个数交换顺序，积不变；同一句口诀可以反过来求商。",
      "MAP-ROTATE": "地图固定朝北；人物转身会改变自己的左右。",
      "MAP-ROUTE": "从起点开始，一段一段跟着路线走。",
      "UNIT-ESTIMATE": "拿铅笔、门或教室的长度作参照，再选单位。",
      "RULER-START-END": "起点不是0时，用终点刻度减起点刻度。",
      "SEGMENT-DRAW": "线段要直直的，两端各有一个端点。",
      "BODY-MEASURE": "每个人的手掌大小一样吗？一拃长度会不会相同？",
      "TWO-STEP": "先求每盒一样多的总数，再处理另外拿来的点心。",
      "ERROR-DETECTIVE": "“少了”以后，总数应该比完整阵列多还是少？",
      "MAP-RELATION": "先确定自己面向哪里，再判断东方在身体哪一侧。",
      "TOOL-CHOICE": "想想物体有多长，以及哪种工具更容易沿着它测量。",
      "RULER-READ": "如果从0开始量，可以直接读终点刻度。",
      "WORD-PROBLEM-SELECT": "圈出题目问什么，再找每一步已知和未知的数量。",
    } as Record<string, string>)[templateId] || "再看看题目里已知什么、要找什么。",
    audio: contentAudioKey("curriculum", question),
    ...(interaction ? { interaction } : {}),
    evidenceRole,
    contextId: templateId.startsWith("SORT") ? "market" : templateId.startsWith("MAP") ? "school-map" : templateId.startsWith("RULER") || templateId.startsWith("SEGMENT") ? "measure-station" : "story-scene",
  };
}

function makeChoice(templateId: string, seed: string): Task {
  const hash = hashSeed(seed);
  return makeChoiceTemplateTask(
    templateId,
    2 + (hash % 5),
    2 + ((hash >>> 4) % 5),
    `${templateId}-${seed}`,
  );
}

const TEMPLATE_TASKS: Record<string, { kind: Kind; a: number; b: number; conceptId: string }> = {
  "GROUPS-TO-MUL": { kind: "group", a: 3, b: 4, conceptId: "MUL-01" },
  "ARRAY-TWO-VIEWS": { kind: "array", a: 3, b: 4, conceptId: "MUL-02" },
  "FACT-MIXED": { kind: "recall", a: 5, b: 4, conceptId: "MUL-03" },
  "DIV-SHARE": { kind: "share", a: 3, b: 4, conceptId: "DIV-01" },
  "DIV-GROUPS": { kind: "share", a: 4, b: 3, conceptId: "DIV-02" },
  "BUILD-FACT-PATTERN": { kind: "jump", a: 7, b: 4, conceptId: "MUL-05" },
};
export const CURRICULUM_TEMPLATE_IDS = [...new Set(CURRICULUM_AREAS.flatMap((area) => area.templates))].sort();

export function makeCurriculumTemplateTask(templateId: string, seed: string, scene: Scene): Task {
  if (templateId === "DIV-SHARE") {
    const task = makePicnicSession().tasks[0];
    task.id = `DIV-SHARE-${seed}`;
    task.templateId = templateId;
    task.conceptId = "DIV-01";
    task.evidenceRole = "construction";
    task.contextId = "picnic-box-sharing";
    return task;
  }
  if (CHOICE_CONTENT[templateId]) {
    const task = makeChoice(templateId, seed);
    task.scene = scene;
    return task;
  }
  const spec = TEMPLATE_TASKS[templateId];
  if (!spec) throw new Error(`模板尚未接入：${templateId}`);
  const hash = hashSeed(seed);
  let a = spec.a;
  let b = spec.b;
  if (["GROUPS-TO-MUL", "ARRAY-TWO-VIEWS", "FACT-MIXED"].includes(templateId)) {
    a = 2 + (hash % (templateId === "FACT-MIXED" ? 5 : 4));
    b = 2 + ((hash >>> 4) % 5);
  }
  if (templateId === "BUILD-FACT-PATTERN") { a = 7 + (hash % 3); b = 3 + ((hash >>> 4) % 4); }
  const task = makeTask(spec.kind, a, b, scene, `${templateId}-${seed}`);
  task.templateId = templateId;
  task.conceptId = spec.conceptId;
  task.evidenceRole = templateId === "FACT-MIXED" ? "recall" : "construction";
  task.contextId = `${templateId.toLowerCase()}-${scene}`;
  if (templateId === "DIV-GROUPS") {
    task.shareMode = "group-count";
    task.question = `有${a * b}块点心，每份放${a}块，可以分成几份？`;
    task.audio = contentAudioKey("curriculum", task.question);
    task.hintText = `每份固定放${a}块。放满一份再继续，最后数一数有几份。`;
  }
  return task;
}

function transferTask(conceptId: string, seed: string, scene: Scene, a: number, b: number): Task {
  const otherScene = scene === "harbor" ? "garden" : "harbor";
  if (conceptId === "SORT-01" || conceptId === "SORT-02") {
    const task = makeChoiceTemplateTask(conceptId === "SORT-01" ? "SORT-REFINE" : "SORT-COUNT", a, b, `transfer-${seed}`);
    task.conceptId = conceptId;
    task.scene = otherScene;
    task.evidenceRole = "transfer";
    task.contextId = `garden-${conceptId.toLowerCase()}`;
    return task;
  }
  if (conceptId.startsWith("MUL-")) {
    const task = makeTask("application", a, b, otherScene, `transfer-${seed}`);
    task.conceptId = conceptId;
    task.templateId = conceptId === "MUL-05" ? "BUILD-FACT-PATTERN" : "GROUPS-TO-MUL";
    task.evidenceRole = "transfer";
    task.contextId = `application-${otherScene}`;
    return task;
  }
  if (conceptId.startsWith("DIV-")) {
    const template = conceptId === "DIV-03" ? "FACT-FAMILY" : "DIV-MODEL-CHOICE";
    const task = makeChoiceTemplateTask(template, a, b, `transfer-${seed}`);
    task.scene = otherScene;
    task.conceptId = conceptId;
    task.evidenceRole = "transfer";
    task.contextId = `sharing-${otherScene}`;
    return task;
  }
  if (conceptId === "DIR-01" || conceptId === "DIR-02") {
    const task = makeChoiceTemplateTask(conceptId === "DIR-01" ? "MAP-RELATION" : "MAP-ROUTE", a, b, `transfer-${seed}`);
    task.conceptId = conceptId;
    task.evidenceRole = "transfer";
    task.contextId = `route-planning-${otherScene}`;
    return task;
  }
  if (conceptId.startsWith("MEAS-")) {
    const template = conceptId === "MEAS-01" ? "BODY-MEASURE" : conceptId === "MEAS-02" ? "TOOL-CHOICE" : conceptId === "MEAS-03" ? "RULER-READ" : "SEGMENT-DRAW";
    const task = makeChoiceTemplateTask(template, a, b, `transfer-${seed}`);
    task.conceptId = conceptId;
    task.evidenceRole = "transfer";
    task.contextId = `home-measurement-${otherScene}`;
    return task;
  }
  const task = makeChoiceTemplateTask("TWO-STEP", a, b, `transfer-${seed}`);
  task.conceptId = "APP-01";
  task.evidenceRole = "transfer";
  task.contextId = `two-step-${otherScene}`;
  return task;
}

function goalTask(conceptId: string, seed: string, scene: Scene, a = 3 + (hashSeed(seed) % 3), b = 2 + (hashSeed(`${seed}:b`) % 5)): Task {
  const template = ({
    "SORT-01": "SORT-REFINE", "SORT-02": "SORT-COUNT", "MUL-04": "PARTIAL-ARRAY",
    "DIV-02": "DIV-MODEL-CHOICE", "DIV-03": "FACT-FAMILY", "DIR-01": "MAP-ROTATE",
    "DIR-02": "MAP-ROUTE", "MEAS-01": "BODY-MEASURE", "MEAS-02": "UNIT-ESTIMATE",
    "MEAS-03": "RULER-START-END", "MEAS-04": "SEGMENT-DRAW",
  } as Record<string, string>)[conceptId];
  if (template && CHOICE_CONTENT[template]) {
    const task = makeChoiceTemplateTask(template, a, b, `${template}-${seed}`);
    task.scene = scene;
    task.conceptId = conceptId;
    task.evidenceRole = "construction";
    task.contextId = `${conceptId.toLowerCase()}-${scene}`;
    return task;
  }
  const kind: Kind = conceptId === "MUL-01" ? "group" : conceptId === "MUL-02" || conceptId === "MUL-03" ? "array" : conceptId === "MUL-05" ? "jump" : conceptId === "DIV-01" ? "share" : conceptId === "APP-01" ? "application" : "recall";
  const task = makeTask(kind, conceptId === "MUL-05" ? 7 + (hashSeed(seed) % 3) : a, b, scene, `${conceptId}-${seed}`);
  task.conceptId = conceptId;
  task.templateId = conceptId === "MUL-01" ? "GROUPS-TO-MUL" : conceptId === "MUL-02" ? "ARRAY-TWO-VIEWS" : conceptId === "MUL-03" ? "FACT-MIXED" : conceptId === "MUL-05" ? "BUILD-FACT-PATTERN" : conceptId === "DIV-01" ? "DIV-SHARE" : "TWO-STEP";
  task.evidenceRole = "construction";
  task.contextId = `${conceptId.toLowerCase()}-${scene}`;
  if (kind === "share") task.shareMode = "each-box";
  return task;
}

function goalPracticeTask(conceptId: string, seed: string, scene: Scene, source: Task): Task {
  if (conceptId === "DIV-01") {
    const task = makeChoiceTemplateTask("DIV-MODEL-CHOICE", Math.max(2, Math.min(6, source.a)), Math.max(2, Math.min(6, source.b)), `review-${seed}`);
    task.scene = scene === "harbor" ? "garden" : "harbor";
    task.conceptId = conceptId;
    task.evidenceRole = "recall";
    task.contextId = "fixed-group-count-review";
    return task;
  }
  if (conceptId.startsWith("MUL-")) {
    const a = source.a === 9 ? 8 : source.a + 1;
    const task = makeTask("recall", a, source.b, scene, `recall-${conceptId}-${seed}`);
    task.conceptId = conceptId;
    task.templateId = conceptId === "MUL-05" ? "BUILD-FACT-PATTERN" : "FACT-MIXED";
    task.evidenceRole = "recall";
    task.contextId = `mixed-recall-${scene}`;
    return task;
  }
  const task = goalTask(conceptId, `practice-${seed}`, scene, source.b, source.a);
  task.evidenceRole = "construction";
  return task;
}

export function makeCurriculumSession(areaId: string, events: Event[] = [], now = Date.now()): Session {
  const area = CURRICULUM_AREAS.find((item) => item.id === areaId);
  if (!area) throw new Error(`未知课程区域：${areaId}`);
  const id = uuid();
  const scene: Scene = area.id.startsWith("U4") ? "harbor" : area.id.startsWith("U5") ? "garden" : "breakfast";
  const areaTasks = area.templates.map((templateId, index) => makeCurriculumTemplateTask(templateId, `${id}-area-${index}`, scene));
  const allTasks = CURRICULUM_TEMPLATE_IDS.map((templateId, index) => makeCurriculumTemplateTask(templateId, `${id}-all-${index}`, scene));
  const summaries = conceptEvidence(events, now);
  const areaConcepts = [...new Set(areaTasks.map((task) => task.conceptId).filter((value): value is string => Boolean(value)))];
  const rank = (conceptId: string) => {
    const summary = summaries.get(conceptId);
    if (summary?.status === "needs-practice") return -1;
    if (!summary || summary.status === "new" || summary.status === "legacy") return 0;
    if (summary.status === "exploring") return 1;
    if (summary.status === "independent") return 2;
    if (summary.status === "flexible") return 3;
    return 4;
  };
  const focusConcept = [...areaConcepts].sort((left, right) =>
    rank(left) - rank(right) ||
    (summaries.get(left)?.independentDays.length || 0) - (summaries.get(right)?.independentDays.length || 0) ||
    left.localeCompare(right),
  )[0];
  const tasks: Task[] = [];
  if (needsFoundation(events)) tasks.push(foundationTask(scene, `${id}:course-foundation`, `${id}-foundation`));
  const dueConcepts = [...summaries.entries()]
    .filter(([conceptId, summary]) => conceptId !== focusConcept && summary.nextReviewAt !== null && summary.nextReviewAt <= now)
    .sort((left, right) => (left[1].nextReviewAt || 0) - (right[1].nextReviewAt || 0))
    .slice(0, 2)
    .map(([conceptId]) => conceptId);
  for (const [dueIndex, conceptId] of dueConcepts.entries()) {
    const due = allTasks.find((task) => task.conceptId === conceptId && task.evidenceRole !== "recognition");
    if (due) tasks.push({ ...due, id: `${id}-due-${dueIndex}`, evidenceRole: due.evidenceRole === "transfer" ? "transfer" : "recall" });
  }
  const focusPool = areaTasks.filter((task) => task.conceptId === focusConcept);
  const intro = focusPool.find((task) => task.evidenceRole === "construction") || goalTask(focusConcept || "APP-01", `${id}-intro`, scene);
  if (intro) tasks.push({ ...intro, id: `${id}-intro`, evidenceRole: "construction" });
  const retrieval = focusPool.find((task) => task.id !== intro.id && task.evidenceRole === "recall") || goalPracticeTask(focusConcept || "APP-01", id, scene, intro);
  if (retrieval) tasks.push({ ...retrieval, id: `${id}-recall`, evidenceRole: "recall", contextId: `${retrieval.contextId || "course"}-recall` });
  const transfer = focusPool.find((task) => task.evidenceRole === "transfer") || transferTask(focusConcept || "APP-01", id, scene, intro?.a || 3, intro?.b || 4);
  if (transfer) tasks.push({ ...transfer, id: `${id}-transfer`, evidenceRole: "transfer" });
  if (tasks.length < 4) {
    const refresher = areaTasks.find((task) => task.conceptId !== focusConcept && Boolean(task.conceptId && summaries.get(task.conceptId)?.seen) && !tasks.some((selected) => selected.conceptId === task.conceptId));
    if (refresher) tasks.push({ ...refresher, id: `${id}-familiar`, evidenceRole: refresher.evidenceRole === "recognition" ? "recognition" : "recall" });
  }
  return {
    id,
    quest: "c9-3",
    scene,
    tasks: prepareTasks(tasks.slice(0, 6)),
    index: 0,
    started: Date.now(),
    mode: "course",
  };
}
export function strategy(a: number, b: number) {
  if (a === 9)
    return `先想10组${b}个是${10 * b}，拿走1组${b}个，就是${9 * b}。`;
  if (a > 5) return `先摆5组${b}个，再摆${a - 5}组${b}个，合起来是${a * b}。`;
  if (a === 4) return `2组${b}个是${2 * b}，再来一样的2组，合起来是${4 * b}。`;
  return `${a}组，每组${b}个：${Array(a).fill(b).join("＋")}＝${a * b}。`;
}
export function mergeEvents(...lists: Event[][]) {
  return [...new Map(lists.flat().map((e) => [e.id, e])).values()].sort(
    (a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id),
  );
}
export function progress(
  events: Event[],
  now = Date.now(),
): Map<string, FactProgress> {
  const result = new Map<string, FactProgress>(
    FACTS.map((f) => [
      f.key,
      {
        key: f.key,
        seen: false,
        understanding: false,
        application: false,
        days: [],
        correct: 0,
        wrong: 0,
        helped: 0,
        due: 0,
        stage: 0,
        status: "new",
        last: 0,
        lastRecall: 0,
        directions: [],
      },
    ]),
  );
  for (const e of mergeEvents(events)) {
    if (e.type !== "answer") continue;
    const d = e.data as unknown as Answer;
    const t = d.task;
    if (
      t.foundation ||
      t.kind === "choice" || t.kind === "interactive" ||
      ["count", "addition", "subtraction", "place"].includes(t.kind)
    )
      continue;
    const p = result.get(t.fact);
    if (!p) continue;
    const at = Date.parse(e.at);
    p.seen = true;
    p.last = at;
    if (!d.correct) {
      p.wrong++;
      p.stage = 0;
      p.due = at + 86400000;
      continue;
    }
    const independent = !d.hinted && d.attempt === 1;
    if (["group", "array", "jump", "share"].includes(t.kind))
      p.understanding = true;
    if (!independent) {
      p.helped++;
      p.due = p.due || at + 86400000;
      continue;
    }
    if (!["recall", "application", "missing"].includes(t.kind)) continue;
    if (t.kind === "application") p.application = true;
    if (t.practiceOnly) {
      p.due = p.due || at + 86400000;
      continue;
    }
    p.correct++;
    p.lastRecall = at;
    if (!p.directions.includes(`${t.a}x${t.b}`))
      p.directions.push(`${t.a}x${t.b}`);
    if (!p.days.includes(day(at))) {
      p.days.push(day(at));
      p.stage = Math.min(4, p.stage + 1);
      p.due = at + [1, 3, 7, 14][p.stage - 1] * 86400000;
    }
  }
  for (const p of result.values()) {
    p.status = !p.seen
      ? "new"
      : p.stage >= 3 &&
          p.days.length >= 3 &&
          p.understanding &&
          p.application &&
          p.lastRecall - Date.parse(p.days[0] + "T00:00:00+08:00") >=
            7 * 86400000
        ? "steady"
        : p.stage >= 2 && p.days.length >= 2 && p.understanding
          ? "initial"
          : "learning";
    if (p.due > now + 90 * 86400000) p.due = now;
  }
  return result;
}
export function completed(events: Event[]) {
  return new Set(
    events
      .filter(
        (e) =>
          e.type === "complete" && QUESTS.some((q) => q.id === e.data.quest),
      )
      .map((e) => String(e.data.quest)),
  );
}
export function unlocked(events: Event[]) {
  const count = completed(events).size;
  return BUILDINGS.slice(
    0,
    Math.min(BUILDINGS.length, Math.floor((count + 2) / 3)),
  );
}
export function profile(events: Event[]) {
  const last = events.filter((e) => e.type === "profile").at(-1);
  return {
    child: String(last?.data.child || "小小建设师"),
    pet: String(last?.data.pet || "泡泡"),
  };
}
export function decorations(events: Event[]) {
  const allowed = unlocked(events);
  const map = new Map<
    string,
    { building: string; slot: number; color: string }
  >();
  for (const e of events) {
    if (e.type === "decorate" && allowed.includes(String(e.data.building)))
      map.set(
        String(e.data.building),
        e.data as { building: string; slot: number; color: string },
      );
  }
  return [...map.values()];
}
export const COSMETICS = [
  { id: "picnic-cloth", name: "海风野餐布", price: 10 },
  { id: "pet-hat", name: "泡泡的小草帽", price: 20 },
  { id: "garden-lights", name: "花园星星灯", price: 40 },
] as const;
export function shellBalance(events: Event[]) {
  return events
    .filter((e) => e.type === "shell")
    .reduce(
      (balance, e) =>
        balance +
        (e.data.action === "earn" ? Number(e.data.amount) : -Number(e.data.amount)),
      0,
    );
}
export function ownedCosmetics(events: Event[]) {
  return new Set(
    events
      .filter((e) => e.type === "shell" && e.data.action === "spend")
      .map((e) => String(e.data.key)),
  );
}
export function adventureDays(events: Event[]) {
  return [
    ...new Set(
      events
        .filter((e) => e.type === "answer")
        .map((e) => day(e.at)),
    ),
  ].sort();
}
export type ConceptEvidence = {
  seen: boolean;
  legacyAnswers: number;
  v3Answers: number;
  recognitionCount: number;
  understanding: boolean;
  understandingDays: string[];
  constructionContexts: string[];
  independentDays: string[];
  application: boolean;
  applicationDays: string[];
  applicationContexts: string[];
  expression: boolean;
  expressionDays: string[];
  physicalDays: string[];
  helped: number;
  wrongAttempts: number;
  needsPractice: boolean;
  lastErrorAt: string | null;
  status: "new" | "legacy" | "exploring" | "independent" | "flexible" | "steady" | "needs-practice";
  nextReviewAt: number | null;
};
export function conceptEvidence(events: Event[], now = Date.now()) {
  const goals = [
    "SORT-01", "SORT-02", "MUL-01", "MUL-02", "MUL-03", "MUL-04",
    "DIV-01", "DIV-02", "DIV-03", "DIR-01", "DIR-02", "MEAS-01",
    "MEAS-02", "MEAS-03", "MEAS-04", "MUL-05", "APP-01",
  ];
  const evidence = new Map<string, ConceptEvidence>(
    goals.map((id) => [id, {
      seen: false,
      legacyAnswers: 0,
      v3Answers: 0,
      recognitionCount: 0,
      understanding: false,
      understandingDays: [] as string[],
      constructionContexts: [] as string[],
      independentDays: [] as string[],
      application: false,
      applicationDays: [] as string[],
      applicationContexts: [] as string[],
      expression: false,
      expressionDays: [] as string[],
      physicalDays: [] as string[],
      helped: 0,
      wrongAttempts: 0,
      needsPractice: false,
      lastErrorAt: null as string | null,
      status: "new" as ConceptEvidence["status"],
      nextReviewAt: null as number | null,
    }]),
  );
  for (const event of mergeEvents(events)) {
    if (event.type === "guardian-observation") {
      const item = evidence.get(String(event.data.conceptId));
      if (!item) continue;
      item.seen = true;
      const date = day(event.at);
      if (event.data.observationType === "spoken-explanation" && !item.expressionDays.includes(date)) item.expressionDays.push(date);
      if (event.data.observationType === "real-world-measurement" && !item.physicalDays.includes(date)) item.physicalDays.push(date);
      item.expression = item.expressionDays.length > 0;
      continue;
    }
    if (event.type !== "answer") continue;
    const answer = event.data as unknown as Answer;
    const id = answer.task.conceptId;
    const item = id ? evidence.get(id) : undefined;
    if (!item) continue;
    item.seen = true;
    const proof = answer.evidence;
    if (!proof || proof.version !== 3 || !answer.task.evidenceRole) {
      item.legacyAnswers++;
      if (answer.task.kind === "choice" && answer.correct) item.recognitionCount++;
      item.status = item.legacyAnswers > 0 ? "legacy" : "exploring";
      continue;
    }
    item.v3Answers++;
    if (answer.correct && proof.responseMode === "selection") item.recognitionCount++;
    if (!answer.correct || answer.hinted || answer.attempt !== 1) {
      if (answer.correct || proof.helpLevel > 0) item.helped++;
      if (!answer.correct) {
        item.wrongAttempts++;
        item.needsPractice = true;
        item.lastErrorAt = event.at;
        item.nextReviewAt = Date.parse(event.at) + 86400000;
      }
      continue;
    }
    if (proof.helpLevel !== 0 || proof.responseMode === "selection" || answer.task.evidenceRole === "recognition") continue;
    item.needsPractice = false;
    item.lastErrorAt = null;
    const date = day(event.at);
    if (!item.independentDays.includes(date)) item.independentDays.push(date);
    if (answer.task.evidenceRole === "construction") {
      if (!item.understandingDays.includes(date)) item.understandingDays.push(date);
      if (answer.task.contextId && !item.constructionContexts.includes(answer.task.contextId)) item.constructionContexts.push(answer.task.contextId);
    }
    if (answer.task.evidenceRole === "transfer" && answer.task.contextId && !item.constructionContexts.includes(answer.task.contextId)) {
      if (!item.applicationDays.includes(date)) item.applicationDays.push(date);
      if (!item.applicationContexts.includes(answer.task.contextId)) item.applicationContexts.push(answer.task.contextId);
    }
    const intervals = [1, 3, 7, 14];
    item.nextReviewAt = Date.parse(event.at) + intervals[Math.min(item.independentDays.length - 1, intervals.length - 1)] * 86400000;
    item.understanding = item.understandingDays.length > 0;
    item.application = item.applicationDays.length > 0;
  }
  for (const [conceptId, item] of evidence) {
    if (conceptId === "MEAS-01") {
      const observed = new Set(item.physicalDays);
      item.independentDays = item.independentDays.filter((date) => observed.has(date));
      item.understandingDays = item.understandingDays.filter((date) => observed.has(date));
      item.applicationDays = item.applicationDays.filter((date) => observed.has(date));
      item.understanding = item.understandingDays.length > 0;
      item.application = item.applicationDays.length > 0;
    }
    const delayed = item.independentDays.some((date) => item.applicationDays.some((applyDate) => Date.parse(`${applyDate}T00:00:00+08:00`) - Date.parse(`${date}T00:00:00+08:00`) >= 7 * 86400000));
    const independent = item.understandingDays.length > 0 && item.independentDays.length >= 2;
    item.status = item.needsPractice
      ? "needs-practice"
      : delayed && independent && item.application
        ? "steady"
        : independent && item.application
          ? "flexible"
          : independent
            ? "independent"
            : item.legacyAnswers > 0 && item.v3Answers === 0
              ? "legacy"
              : item.seen
                ? "exploring"
                : "new";
    if (item.nextReviewAt !== null && item.nextReviewAt < now && item.status !== "steady") item.nextReviewAt = now;
  }
  return evidence;
}
export function needsFoundation(events: Event[]) {
  const base = events.filter(
    (e) => e.type === "answer" && (e.data as unknown as Answer).task.foundation,
  );
  if (!base.length) return false;
  const last = base.slice(-4);
  return (
    last.filter(
      (e) =>
        (e.data as unknown as Answer).correct &&
        !(e.data as unknown as Answer).hinted &&
        (e.data as unknown as Answer).attempt === 1,
    ).length < Math.min(3, last.length)
  );
}
export function prepareTasks(tasks: Task[]) {
  return tasks.map((t, i) => ({
    ...t,
    practiceOnly:
      !t.foundation &&
      ["recall", "application", "missing"].includes(t.kind) &&
      tasks
        .slice(0, i)
        .some((previous) => previous.fact === t.fact && !previous.foundation),
  }));
}
function chooseFacts(
  pool: typeof FACTS,
  p: Map<string, FactProgress>,
  part: number,
  sequence: number,
  seed: string,
) {
  const ordered = shuffled(pool, `${seed}:facts`);
  const unseen = ordered.filter((f) => !p.get(f.key)!.seen);
  const picks: typeof FACTS = [];
  const anchors = shuffled(
    CURRICULUM_FACTS.slice(sequence * 2, sequence * 2 + 2),
    `${seed}:anchors`,
  ).filter(
    (f) => !p.get(f.key)!.seen,
  );
  picks.push(...anchors);
  // Part 1 starts with a less obvious product so the child learns to reason,
  // while the final part deliberately brings back the diagonal fact.
  const complex = unseen.filter((f) => f.a * f.b >= 12 && f.a > 1 && f.b > 1);
  const diagonal = unseen.find((f) => f.a === f.b);
  const first = part === 2 && diagonal ? diagonal : (complex[0] || unseen[0]);
  if (!picks.length && first) picks.push(first);
  const remaining = ordered.filter(
    (f) => !picks.some((picked) => picked.key === f.key),
  );
  const second =
    (part !== 2
      ? remaining.find(
          (f) => f.a > 1 && f.b > 1 && f.a * f.b >= 12,
        )
      : undefined) || remaining[0];
  if (second) picks.push(second);
  // Once a chapter's new facts are exhausted, use a due or least-recent fact
  // instead of repeating the same easy pair every time.
  for (const f of ordered) {
    if (picks.length >= 2) break;
    if (!picks.some((picked) => picked.key === f.key)) picks.push(f);
  }
  return picks.slice(0, 2);
}
function foundationTask(scene: Scene, seed: string, id: string) {
  const kind = FOUNDATION_KINDS[hashSeed(`${seed}:kind`) % FOUNDATION_KINDS.length];
  const a = 2 + (hashSeed(`${seed}:a`) % 6);
  const b = 2 + (hashSeed(`${seed}:b`) % 6);
  return makeTask(kind, a, b, scene, id, true);
}
export function makeSession(
  events: Event[],
  scene: Scene,
  mode: Session["mode"] = "story",
  questId?: string,
  checkpoint?: number,
): Session {
  const id = uuid();
  const p = progress(events);
  const done = completed(events);
  const quest =
    QUESTS.find((q) => q.id === questId) ||
    QUESTS.find((q) => !done.has(q.id)) ||
    QUESTS[0];
  let tasks: Task[] = [];
  if (mode === "baseline")
    tasks = [
      makeTask("count", 6, 1, scene, "base-count", true),
      makeTask("addition", 3, 4, scene, "base-add", true),
      makeTask("group", 2, 3, scene, "base-group"),
      makeTask("recall", 2, 3, scene, "base-recall"),
    ];
  else if (mode === "assessment") {
    const pairs =
      checkpoint === 7
        ? [
            [4, 2],
            [3, 5],
            [4, 6],
            [7, 3],
            [5, 4],
            [3, 7],
          ]
        : [
            [2, 4],
            [5, 3],
            [6, 4],
            [3, 7],
            [4, 5],
            [7, 3],
          ];
    tasks = pairs.map(([a, b], i) =>
      makeTask(
        (
          [
            "recall",
            "recall",
            "recall",
            "missing",
            "application",
            "application",
          ] as Kind[]
        )[i],
        a,
        b,
        checkpoint === 14 ? "garden" : scene,
        `test-${i}`,
      ),
    );
  } else {
    const seed = `${id}:${quest.id}`;
    const pool = FACTS.filter(
      (f) => f.a === quest.factor || f.b === quest.factor,
    );
    const review = FACTS.filter((f) => p.get(f.key)!.seen).sort((a, b) => {
      const x = p.get(a.key)!,
        y = p.get(b.key)!;
      return (
        Number(y.due <= Date.now()) - Number(x.due <= Date.now()) ||
        Number(y.directions.length < (b.a === b.b ? 1 : 2)) -
          Number(x.directions.length < (a.a === a.b ? 1 : 2)) ||
        x.last - y.last ||
        a.key.localeCompare(b.key)
      );
    });
    if (mode === "review" || mode === "challenge") {
      const suitable =
        mode === "challenge"
          ? review.filter((f) =>
              ["initial", "steady"].includes(p.get(f.key)!.status),
            )
          : review;
      const candidates = suitable.length ? suitable : pool;
      // Preserve the review ranking so a missing multiplication direction is
      // repaired before another random shuffle can starve it indefinitely.
      const picks =
        mode === "review"
          ? [...candidates].slice(0, 6)
          : shuffled(candidates, `${seed}:session`).slice(0, 6);
      tasks = picks.map((f, i) => {
        const state = p.get(f.key)!;
        const a = state.directions.includes(`${f.a}x${f.b}`) ? f.b : f.a;
        const b = a === f.a ? f.b : f.a;
        const kind = shuffled(
          ["application", "recall", "missing"] as Kind[],
          `${seed}:${i}:kind`,
        )[0];
        return makeTask(
          kind,
          a,
          b,
          shuffled(SCENE_ORDER, `${seed}:${i}:scene`)[0],
          `review-${i}-${f.key}`,
        );
      });
    } else {
      const picks = chooseFacts(
        pool,
        p,
        quest.part,
        quest.chapter * 3 + quest.part,
        seed,
      );
      const [f, g] = picks;
      if (needsFoundation(events)) {
        tasks.push(
          foundationTask(scene, `${seed}:count`, "support-count"),
          foundationTask(scene, `${seed}:add`, "support-add"),
          foundationTask(scene, `${seed}:subtract`, "support-subtract"),
          foundationTask(scene, `${seed}:place`, "support-place"),
        );
      }
      const due = review.find((f) => p.get(f.key)!.due <= Date.now());
      if (due)
        tasks.push(
          makeTask(
            "recall",
            due.b,
            due.a,
            shuffled(SCENE_ORDER, `${seed}:warmup`)[0],
            `warmup-${due.key}`,
          ),
        );
      const visuals = shuffled(VISUAL_KINDS, `${seed}:visuals`);
      const scenes = shuffled(SCENE_ORDER, `${seed}:scenes`);
      tasks.push(
        makeTask(visuals[0], f.a, f.b, scenes[0], `visual-1-${f.key}`),
        makeTask(visuals[1], f.a, f.b, scenes[1], `visual-2-${f.key}`),
        makeTask(visuals[2], f.a, f.b, scenes[2], `visual-3-${f.key}`),
        makeTask("recall", f.a, f.b, scenes[0], `recall-${f.key}`),
        makeTask(
          visuals[hashSeed(`${seed}:second-visual`) % visuals.length],
          g.a,
          g.b,
          scenes[1],
          `second-visual-${g.key}`,
        ),
        makeTask("recall", g.b, g.a, scenes[2], `second-recall-${g.key}`),
      );
      const finalKinds = shuffled(["application", "missing"] as Kind[], `${seed}:final`);
      for (const kind of finalKinds) {
        tasks.push(
          makeTask(
            kind,
            kind === "application" ? f.a : g.a,
            kind === "application" ? f.b : g.b,
            kind === "application" ? scenes[2] : scenes[0],
            `${kind}-${kind === "application" ? f.key : g.key}`,
          ),
        );
      }
      // Every third mission briefly reconnects multiplication to the
      // grade-two/three bridge skills: quantities, operations and place value.
      if (quest.part === 2)
        tasks.splice(
          1,
          0,
          foundationTask(scene, `${seed}:bridge`, `bridge-${quest.id}`),
        );
    }
  }
  return {
    id,
    quest: quest.id,
    scene,
    tasks: prepareTasks(tasks),
    index: 0,
    started: Date.now(),
    mode,
    checkpoint,
  };
}
export function validTask(value: unknown): value is Task {
  if (!value || typeof value !== "object") return false;
  const t = value as Task;
  const kinds: Kind[] = ["group", "array", "jump", "recall", "application", "missing", "count", "addition", "subtraction", "place", "share", "choice", "interactive"];
  return Boolean(
    kinds.includes(t.kind) && Number.isInteger(t.a) && t.a >= 1 && t.a <= 9 &&
    Number.isInteger(t.b) && t.b >= 1 && t.b <= 9 && SCENES[t.scene] &&
    typeof t.id === "string" && t.id.length > 0 && t.id.length <= 100 &&
    typeof t.question === "string" && t.question.length <= 240 &&
    typeof t.audio === "string" && t.audio.length <= 180 &&
    t.fact === factKey(t.a, t.b) && Number.isFinite(t.answer) && t.answer === answerOf(t) &&
    (t.kind !== "choice" || (
      Array.isArray(t.options) && t.options.length >= 2 && t.options.length <= 5 &&
      t.options.every((option) => typeof option === "string" && option.length <= 120) &&
      Number.isInteger(t.choiceAnswer) && t.choiceAnswer! >= 0 && t.choiceAnswer! < t.options.length
    )) &&
    (t.kind !== "interactive" || validInteractionSpec(t.interaction)) &&
    (t.interaction === undefined || (t.kind === "interactive" && validInteractionSpec(t.interaction))) &&
    (t.evidenceRole === undefined || ["recognition", "construction", "recall", "transfer"].includes(t.evidenceRole)) &&
    (t.contextId === undefined || (typeof t.contextId === "string" && t.contextId.length <= 64)) &&
    (t.hintText === undefined || (typeof t.hintText === "string" && t.hintText.length <= 240)) &&
    (t.workedText === undefined || (typeof t.workedText === "string" && t.workedText.length <= 240))
  );
}
export function validEvent(e: unknown): e is Event {
  if (!e || typeof e !== "object") return false;
  const v = e as Event;
  if (
    typeof v.id !== "string" ||
    v.id.length > 100 ||
    !v.id ||
    typeof v.at !== "string" ||
    !Number.isFinite(Date.parse(v.at)) ||
    Date.parse(v.at) > Date.now() + 300000 ||
    !v.data ||
    typeof v.data !== "object"
  )
    return false;
  if (v.type === "answer") {
    const a = v.data as unknown as Answer,
      t = a.task;
    if (
      !validTask(t) ||
      !Number.isFinite(a.value) ||
      !Number.isFinite(a.firstAnswer) ||
      a.correct !== (a.value === t.answer) ||
      typeof a.hinted !== "boolean" ||
      !Number.isInteger(a.attempt) ||
      a.attempt < 1 ||
      a.attempt > 999 ||
      !Number.isFinite(a.duration) ||
      a.duration < 0 ||
      a.duration > 86400000 ||
      typeof a.session !== "string" ||
      a.session.length > 100
    )
      return false;
    if (t.evidenceRole) {
      const evidence = a.evidence;
      if (
        !evidence || evidence.version !== 3 ||
        !["selection", "manipulation", "numeric"].includes(evidence.responseMode) ||
        ![0, 1, 2].includes(evidence.helpLevel) ||
        !["none", "sentence-frame"].includes(evidence.explanation) ||
        (a.hinted && evidence.helpLevel === 0) ||
        (!a.hinted && evidence.helpLevel !== 0) ||
        (evidence.responseMode === "selection" && t.evidenceRole !== "recognition")
      ) return false;
      if (t.interaction) {
        const expectedMode = t.interaction.type === "multi-step" || t.interaction.type === "estimate" ? "numeric" : "manipulation";
        if (evidence.responseMode !== expectedMode || !validInteractionResponse(evidence.response, t.interaction)) return false;
        const modeledCorrect = interactionIsCorrect(t.interaction, evidence.response);
        if (a.correct !== modeledCorrect || a.value !== (modeledCorrect ? t.answer : -1)) return false;
      } else if (evidence.response !== undefined) {
        const response = evidence.response;
        if (t.kind === "group" && !(response.type === "groups" && response.counts.length === t.a && response.counts.every((n) => Number.isInteger(n) && n >= 0 && n <= 9))) return false;
        if (t.kind === "array" && !(response.type === "array" && response.counts.length === t.a && response.counts.every((n) => Number.isInteger(n) && n >= 0 && n <= 9) && ["rows", "columns"].includes(response.orientation || "rows"))) return false;
        if (t.kind === "share" && !(response.type === "share" && response.counts.length <= 9 && response.counts.every((n) => Number.isInteger(n) && n >= 0 && n <= 9))) return false;
        if (t.kind === "jump" && !(response.type === "jump" && Number.isInteger(response.hops) && response.hops >= 0 && response.hops <= 10)) return false;
        if (["recall", "missing", "application"].includes(t.kind) && !(response.type === "numeric" && Number.isFinite(response.value) && (response.relation === undefined || ["add", "multiply"].includes(response.relation)))) return false;
        if (["group", "array", "share", "jump", "recall", "missing", "application"].includes(t.kind) && !response) return false;
        if (response.type === "groups" || response.type === "array") {
          const model = response.counts.length === t.a && response.counts.every((count) => count === t.b);
          if (a.correct !== model || a.value !== (model ? t.answer : -1)) return false;
        }
        if (response.type === "share") {
          const model = t.shareMode === "group-count"
            ? response.counts.length === t.b && response.counts.every((count) => count === t.a)
            : response.counts.length === t.a && response.counts.every((count) => count === t.b);
          if (a.correct !== model || a.value !== (model ? t.answer : -1)) return false;
        }
        if (response.type === "jump") {
          const model = response.hops === t.a;
          if (a.correct !== model || a.value !== (model ? t.answer : -1)) return false;
        }
        if (response.type === "numeric") {
          const model = response.value === t.answer && (t.kind !== "application" || response.relation === "multiply");
          const storedValue = t.kind === "application" && response.relation !== "multiply" ? -1 : response.value;
          if (a.correct !== model || a.value !== storedValue) return false;
        }
      }
    } else if (t.interaction) return false;
    return true;
  }
  if (v.type === "complete")
    return (
      QUESTS.some((q) => q.id === v.data.quest) &&
      typeof v.data.session === "string"
    );
  if (v.type === "decorate")
    return (
      BUILDINGS.includes(String(v.data.building)) &&
      Number.isInteger(v.data.slot) &&
      Number(v.data.slot) >= 0 &&
      Number(v.data.slot) < 9 &&
      ["coral", "sage", "sun"].includes(String(v.data.color))
    );
  if (v.type === "profile")
    return ["child", "pet"].every(
      (k) =>
        typeof v.data[k] === "string" &&
        String(v.data[k]).trim().length > 0 &&
        String(v.data[k]).length <= 16,
    );
  if (v.type === "baseline") return v.data.finished === true;
  if (v.type === "shell")
    return (
      (v.data.action === "earn" &&
        Number(v.data.amount) === 10 &&
        /^\d{4}-\d{2}-\d{2}$/.test(String(v.data.key)) &&
        v.id === `shell:daily:${String(v.data.key)}`) ||
      (v.data.action === "spend" &&
        COSMETICS.some(
          (item) =>
            item.id === v.data.key && item.price === Number(v.data.amount) &&
            v.id === `shell:spend:${item.id}`,
        ))
    );
  if (v.type === "assessment")
    return [0, 7, 14].includes(Number(v.data.checkpoint));
  if (v.type === "guardian-observation")
    return (
      ["SORT-01", "SORT-02", "MUL-01", "MUL-02", "MUL-03", "MUL-04", "DIV-01", "DIV-02", "DIV-03", "DIR-01", "DIR-02", "MEAS-01", "MEAS-02", "MEAS-03", "MEAS-04", "MUL-05", "APP-01"].includes(String(v.data.conceptId)) &&
      ["spoken-explanation", "real-world-measurement"].includes(String(v.data.observationType)) &&
      typeof v.data.contextId === "string" && v.data.contextId.length > 0 && v.data.contextId.length <= 64 &&
      !("audio" in v.data) && !("transcript" in v.data)
    );
  if (v.type === "checkpoint") {
    const s = v.data.session as Session | null;
    if (s === null) return true;
    if (
      !s ||
      typeof s.id !== "string" ||
      s.id.length > 100 ||
      !QUESTS.some((q) => q.id === s.quest) ||
      !["story", "review", "baseline", "assessment", "challenge", "course"].includes(
        s.mode,
      ) ||
      !SCENES[s.scene] ||
      !Number.isFinite(s.started) ||
      !Array.isArray(s.tasks) ||
      s.tasks.length < 1 ||
      s.tasks.length > 40 ||
      !Number.isInteger(s.index) ||
      s.index < 0 ||
      s.index >= s.tasks.length
    )
      return false;
    return s.tasks.every((task) => validTask(task));
  }
  return false;
}
